import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { WashingMachine, Wind, Package, Sparkle, Bed, CheckCircle, Minus, Plus, CaretLeft, CaretRight, Heart } from "@phosphor-icons/react";
import api, { fmtErr, isAuPhone } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const icons = { wash: WashingMachine, dry: Wind, fold: Package };
const catMeta = {
  bulky: { label: "Bulky Items", hint: "Quilts, doonas & pillows — washed, dried & folded", Icon: Bed },
  iron: { label: "Ironing", hint: "Crisp, professional ironing — priced per item", Icon: Sparkle },
  steam: { label: "Steaming", hint: "Gentle steam for shirts, suits & dresses", Icon: Wind },
};
const steps = ["Services", "Load & Items", "Address", "Date & Time", "Your Details", "Confirm"];
const input = "w-full rounded-2xl border-2 border-brand-green/15 bg-white px-4 py-3 text-sm font-semibold outline-none transition-colors duration-200 focus:border-brand-green focus:ring-4 focus:ring-brand-green/10";

function tomorrow() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
}

export default function Book() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const rebook = location.state?.rebook;

  const [catalog, setCatalog] = useState(null);
  const [step, setStep] = useState(0);
  const [selected, setSelected] = useState((rebook?.services || []).filter((c) => ["wash", "dry", "fold"].includes(c)));
  const [cats, setCats] = useState([]);
  const [weight, setWeight] = useState(rebook?.weight_kg || 8);
  const [qty, setQty] = useState(() => {
    const q = {};
    (rebook?.addons || []).forEach((a) => { q[a.code] = a.qty; });
    return q;
  });
  const [address, setAddress] = useState({ street: rebook?.street || "", suburb: rebook?.suburb || "", postcode: rebook?.postcode || "", state: "NSW" });
  const [areaCheck, setAreaCheck] = useState(null);
  const [saveAddress, setSaveAddress] = useState(false);
  const [pickupDate, setPickupDate] = useState(tomorrow());
  const [slots, setSlots] = useState([]);
  const [blackout, setBlackout] = useState(false);
  const [timeSlot, setTimeSlot] = useState("");
  const [contact, setContact] = useState({ name: rebook?.name || "", email: rebook?.email || "", phone: rebook?.phone || "", notes: "" });
  const [estimate, setEstimate] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const [payChoice, setPayChoice] = useState("now");

  useEffect(() => {
    document.title = "Book Online | Zaekids Laundromat — Laundry Pick Up & Delivery NSW";
    api.get("/services").then((r) => setCatalog(r.data));
  }, []);

  // Auto-enable any categories that already have items (from rebook)
  useEffect(() => {
    if (!catalog) return;
    const active = [];
    ["bulky", "iron", "steam"].forEach((c) => {
      if ((catalog.addons?.[c] || []).some((i) => (qty[i.code] || 0) > 0)) active.push(c);
    });
    if (active.length) setCats((cs) => Array.from(new Set([...cs, ...active])));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalog]);

  useEffect(() => {
    if (user) setContact((c) => ({ ...c, name: c.name || user.name, email: c.email || user.email, phone: c.phone || user.phone }));
  }, [user]);

  useEffect(() => {
    if (!pickupDate) return;
    api.get("/timeslots", { params: { date: pickupDate } }).then(({ data }) => {
      setSlots(data.slots);
      setBlackout(data.blackout);
      setTimeSlot("");
    });
  }, [pickupDate]);

  useEffect(() => {
    if (!/^\d{4}$/.test(address.postcode)) { setAreaCheck(null); return; }
    api.get("/service-area/check", { params: { postcode: address.postcode } })
      .then((r) => setAreaCheck(r.data)).catch(() => setAreaCheck(null));
  }, [address.postcode]);

  const nameByCode = useMemo(() => {
    const m = {};
    if (catalog) ["bulky", "iron", "steam"].forEach((c) => (catalog.addons?.[c] || []).forEach((i) => { m[i.code] = i.name; }));
    return m;
  }, [catalog]);

  const catByCode = useMemo(() => {
    const m = {};
    if (catalog) ["bulky", "iron", "steam"].forEach((c) => (catalog.addons?.[c] || []).forEach((i) => { m[i.code] = c; }));
    return m;
  }, [catalog]);

  const addonList = useMemo(
    () => Object.entries(qty).filter(([code, q]) => q > 0 && cats.includes(catByCode[code])).map(([code, q]) => ({ code, qty: q })),
    [qty, cats, catByCode]
  );
  const addonCount = useMemo(() => addonList.reduce((n, a) => n + a.qty, 0), [addonList]);

  const fetchEstimate = useCallback(() => {
    if (selected.length === 0 && addonList.length === 0) { setEstimate(null); return; }
    const pc = /^\d{4}$/.test(address.postcode) ? address.postcode : "";
    api.post("/estimate", { services: selected, weight_kg: weight, addons: addonList, postcode: pc })
      .then((r) => setEstimate(r.data)).catch(() => {});
  }, [selected, weight, addonList, address.postcode]);

  useEffect(() => { const t = setTimeout(fetchEstimate, 250); return () => clearTimeout(t); }, [fetchEstimate]);

  const hasKg = useMemo(() => selected.some((c) => ["wash", "dry", "fold"].includes(c)), [selected]);

  const toggle = (code) => setSelected((s) => (s.includes(code) ? s.filter((c) => c !== code) : [...s, code]));
  const toggleCat = (c) => setCats((cs) => {
    if (cs.includes(c)) {
      const codes = (catalog.addons?.[c] || []).map((i) => i.code);
      setQty((q) => { const n = { ...q }; codes.forEach((code) => delete n[code]); return n; });
      return cs.filter((x) => x !== c);
    }
    return [...cs, c];
  });
  const setItemQty = (code, v) => setQty((q) => ({ ...q, [code]: Math.max(0, v) }));

  const canNext = () => {
    if (step === 0) return selected.length > 0 || cats.length > 0;
    if (step === 1) return (hasKg && weight > 0) || addonCount > 0;
    if (step === 2) return address.street && address.suburb && /^\d{4}$/.test(address.postcode) && areaCheck?.serviced;
    if (step === 3) return pickupDate && timeSlot;
    if (step === 4) return contact.name && contact.email && isAuPhone(contact.phone);
    return true;
  };

  const next = () => {
    if (step === 4 && !isAuPhone(contact.phone)) {
      toast.error("Please enter a valid Australian phone number (e.g. 04XX XXX XXX)");
      return;
    }
    setStep((s) => Math.min(s + 1, 5));
  };

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/bookings", {
        services: selected, weight_kg: hasKg ? weight : 0, addons: addonList,
        ...address, pickup_date: pickupDate, time_slot: timeSlot,
        name: contact.name, email: contact.email, phone: contact.phone, notes: contact.notes,
      });
      if (user && saveAddress) {
        api.post("/my/addresses", { label: "Home", ...address }).catch(() => {});
      }
      if (payChoice === "now") {
        try {
          const { data: pay } = await api.post("/payments/checkout", {
            order_number: data.order_number, phone: data.phone, origin_url: window.location.origin,
          });
          window.location.href = pay.checkout_url;
          return;
        } catch {
          toast.error("Booking saved, but we couldn't start payment. You can pay any time from Track Order.");
        }
      }
      setConfirmed(data);
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(fmtErr(err));
    }
    setBusy(false);
  };

  if (!catalog) return <div className="py-32 text-center font-bold text-brand-green">Getting things ready…</div>;

  if (confirmed) {
    return (
      <div data-testid="booking-confirmation" className="mx-auto max-w-xl px-4 py-20 text-center sm:px-6">
        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", bounce: 0.5 }}
          className="mx-auto flex h-24 w-24 items-center justify-center rounded-full bg-brand-green text-white">
          <CheckCircle size={56} weight="fill" />
        </motion.div>
        <h1 className="mt-6 text-3xl font-black text-brand-green sm:text-4xl">You're all booked!</h1>
        <p className="mt-3 text-slate-600">A confirmation email is on its way to <strong>{confirmed.email}</strong>.</p>
        <div className="mt-8 rounded-3xl bg-white p-8 text-left shadow-[0_8px_30px_rgb(27,94,32,0.08)]">
          <p className="text-center text-sm font-bold text-slate-500">Your order number</p>
          <p data-testid="confirmation-order-number" className="text-center text-4xl font-black tracking-wide text-brand-green">{confirmed.order_number}</p>
          <div className="mt-6 space-y-2 text-sm text-slate-700">
            <p><strong>Pick up:</strong> {confirmed.pickup_date} · {confirmed.time_slot}</p>
            <p><strong>Address:</strong> {confirmed.street}, {confirmed.suburb} {confirmed.state} {confirmed.postcode}</p>
            <p><strong>Estimated total:</strong> ${confirmed.pricing.total.toFixed(2)} <span className="text-slate-400">(final price confirmed on weighing)</span></p>
          </div>
        </div>
        <div className="mt-8 flex flex-wrap justify-center gap-4">
          <button data-testid="confirmation-track-btn" onClick={() => navigate("/track", { state: { order_number: confirmed.order_number, phone: confirmed.phone } })}
            className="btn-bounce rounded-full bg-brand-green px-8 py-4 font-extrabold text-white">Track My Order</button>
          <button onClick={() => navigate("/")} className="btn-bounce rounded-full border-2 border-brand-green px-8 py-4 font-extrabold text-brand-green">Back Home</button>
        </div>
        <p className="mt-6 font-script text-2xl font-bold text-brand-yellow-dark">Clean Clothes. Happy Life. <Heart size={20} weight="fill" className="inline text-brand-green" /></p>
      </div>
    );
  }

  return (
    <div data-testid="book-page" className="mx-auto max-w-5xl px-4 py-12 sm:px-6">
      <div className="text-center">
        <span className="font-script text-3xl font-bold text-brand-yellow-dark">Book a Pick Up</span>
        <h1 className="mt-2 text-3xl font-black tracking-tight text-brand-green sm:text-4xl">Fresh laundry in a few taps</h1>
      </div>

      {/* Stepper */}
      <div className="mt-8 flex items-center justify-center gap-1 sm:gap-2">
        {steps.map((s, i) => (
          <div key={s} className="flex items-center gap-1 sm:gap-2">
            <div className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-extrabold sm:h-9 sm:w-9 ${i < step ? "bg-brand-green text-white" : i === step ? "bg-brand-yellow text-brand-green" : "bg-slate-100 text-slate-400"}`}>
              {i < step ? <CheckCircle size={18} weight="fill" /> : i + 1}
            </div>
            <span className={`hidden text-xs font-bold md:block ${i === step ? "text-brand-green" : "text-slate-400"}`}>{s}</span>
            {i < steps.length - 1 && <span className="h-0.5 w-3 rounded bg-slate-200 sm:w-6" />}
          </div>
        ))}
      </div>

      <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_320px]">
        <div className="rounded-3xl bg-white p-6 shadow-[0_8px_30px_rgb(27,94,32,0.08)] sm:p-8">
          {step === 0 && (
            <div data-testid="step-services">
              <h2 className="text-xl font-extrabold text-brand-green">Which services do you need?</h2>
              <div className="mt-6 grid gap-4 sm:grid-cols-2">
                {catalog.services.map((s) => {
                  const Icon = icons[s.code] || WashingMachine;
                  const active = selected.includes(s.code);
                  return (
                    <button key={s.code} data-testid={`select-service-${s.code}`} onClick={() => toggle(s.code)}
                      className={`flex items-center gap-4 rounded-2xl border-2 p-4 text-left transition-colors duration-200 ${active ? "border-brand-green bg-soft-green" : "border-brand-green/10 hover:border-brand-green/40"}`}>
                      <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full ${active ? "bg-brand-green text-white" : "bg-soft-yellow text-brand-green"}`}>
                        <Icon size={26} weight="duotone" />
                      </span>
                      <div className="flex-1">
                        <p className="font-extrabold text-brand-green">{s.name}</p>
                        <p className="text-xs text-slate-500">${s.price.toFixed(2)} / {s.unit}</p>
                      </div>
                      {active && <CheckCircle size={24} weight="fill" className="text-brand-green" />}
                    </button>
                  );
                })}
              </div>
              {catalog.bundles.map((b) => (
                <p key={b.code} className="mt-4 rounded-2xl bg-soft-yellow px-4 py-3 text-sm font-bold text-brand-green">
                  💛 Tip: select {b.name.replace(/ \+ /g, ", ").toLowerCase()} together and we'll auto-apply the {b.name} bundle at ${b.price.toFixed(2)}/kg!
                </p>
              ))}

              <h3 className="mt-8 text-lg font-extrabold text-brand-green">Add extra services</h3>
              <div className="mt-4 grid gap-4 sm:grid-cols-3">
                {["iron", "steam", "bulky"].map((c) => {
                  const meta = catMeta[c];
                  const active = cats.includes(c);
                  return (
                    <button key={c} data-testid={`select-category-${c}`} onClick={() => toggleCat(c)}
                      className={`flex flex-col items-start gap-2 rounded-2xl border-2 p-4 text-left transition-colors duration-200 ${active ? "border-brand-green bg-soft-green" : "border-brand-green/10 hover:border-brand-green/40"}`}>
                      <span className={`flex h-11 w-11 items-center justify-center rounded-full ${active ? "bg-brand-green text-white" : "bg-soft-yellow text-brand-green"}`}>
                        <meta.Icon size={24} weight="duotone" />
                      </span>
                      <p className="font-extrabold text-brand-green">{meta.label}</p>
                      <p className="text-xs text-slate-500">{meta.hint}</p>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {step === 1 && (
            <div data-testid="step-load">
              <h2 className="text-xl font-extrabold text-brand-green">Your load & items</h2>
              {hasKg && (
                <div className="mt-6">
                  <div className="flex items-end justify-between">
                    <label className="text-sm font-extrabold text-brand-green">Estimated weight (Wash · Dry · Fold)</label>
                    <span data-testid="weight-value" className="text-2xl font-black text-brand-green">{weight} kg</span>
                  </div>
                  <input data-testid="weight-slider" type="range" min="1" max="30" step="1" value={weight}
                    onChange={(e) => setWeight(Number(e.target.value))} className="mt-3 w-full accent-brand-green" />
                  <p className="mt-2 text-xs text-slate-500">
                    Minimum billed load is {catalog.min_weight_kg} kg (${(catalog.min_weight_kg * (catalog.bundles[0]?.price || 6)).toFixed(0)}). As a guide: a full laundry basket ≈ 5–7 kg, a big family load ≈ 10–15 kg. We weigh everything on pick up, so this is just an estimate.
                  </p>
                </div>
              )}
              {cats.map((c) => (
                <div key={c} className="mt-8" data-testid={`items-${c}`}>
                  <h3 className="text-sm font-black uppercase tracking-wide text-brand-green">{catMeta[c].label}</h3>
                  <div className="mt-3 space-y-2">
                    {(catalog.addons?.[c] || []).map((i) => (
                      <QtyRow key={i.code} item={i} value={qty[i.code] || 0}
                        onChange={(v) => setItemQty(i.code, v)} />
                    ))}
                  </div>
                </div>
              ))}
              {!hasKg && cats.length === 0 && (
                <p className="mt-6 rounded-2xl bg-soft-yellow px-4 py-3 text-sm font-bold text-brand-green">Go back and pick a service or an extra to add items.</p>
              )}
            </div>
          )}

          {step === 2 && (
            <div data-testid="step-address">
              <h2 className="text-xl font-extrabold text-brand-green">Where do we pick up?</h2>
              {user && user.addresses?.length > 0 && (
                <div className="mt-4 space-y-2">
                  <p className="text-sm font-bold text-slate-500">Saved addresses</p>
                  {user.addresses.map((a, i) => (
                    <button key={i} data-testid={`saved-address-${i}`}
                      onClick={() => setAddress({ street: a.street, suburb: a.suburb, postcode: a.postcode, state: a.state })}
                      className="block w-full rounded-2xl border-2 border-brand-green/15 px-4 py-3 text-left text-sm font-semibold hover:border-brand-green">
                      <strong>{a.label}:</strong> {a.street}, {a.suburb} {a.state} {a.postcode}
                    </button>
                  ))}
                </div>
              )}
              <label className="mb-1 mt-5 block text-sm font-extrabold text-brand-green">Street address *</label>
              <input data-testid="address-street-input" className={input} value={address.street}
                onChange={(e) => setAddress({ ...address, street: e.target.value })} placeholder="12 Sunny Lane" />
              <div className="mt-4 grid gap-4 sm:grid-cols-3">
                <div className="sm:col-span-1">
                  <label className="mb-1 block text-sm font-extrabold text-brand-green">Suburb *</label>
                  <input data-testid="address-suburb-input" className={input} value={address.suburb}
                    onChange={(e) => setAddress({ ...address, suburb: e.target.value })} placeholder="Parramatta" />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-extrabold text-brand-green">Postcode *</label>
                  <input data-testid="address-postcode-input" className={input} value={address.postcode} maxLength={4}
                    onChange={(e) => setAddress({ ...address, postcode: e.target.value.replace(/\D/g, "") })} placeholder="2150" />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-extrabold text-brand-green">State</label>
                  <input className={`${input} bg-slate-50`} value="NSW" disabled />
                </div>
              </div>
              {areaCheck && (
                <p data-testid="service-area-result" className={`mt-4 rounded-2xl px-4 py-3 text-sm font-bold ${areaCheck.serviced ? "bg-soft-green text-brand-green" : "bg-red-50 text-red-600"}`}>
                  {areaCheck.serviced ? "✅ " : "😔 "}{areaCheck.message}
                </p>
              )}
              {user && (
                <label className="mt-4 flex items-center gap-2 text-sm font-bold text-slate-600">
                  <input data-testid="save-address-checkbox" type="checkbox" checked={saveAddress} onChange={(e) => setSaveAddress(e.target.checked)}
                    className="h-4 w-4 accent-brand-green" /> Save this address to my account
                </label>
              )}
            </div>
          )}

          {step === 3 && (
            <div data-testid="step-datetime">
              <h2 className="text-xl font-extrabold text-brand-green">When should we come?</h2>
              <label className="mb-1 mt-5 block text-sm font-extrabold text-brand-green">Pick-up date *</label>
              <input data-testid="pickup-date-input" type="date" min={tomorrow()} className={input} value={pickupDate}
                onChange={(e) => setPickupDate(e.target.value)} />
              {blackout ? (
                <p data-testid="blackout-message" className="mt-4 rounded-2xl bg-red-50 px-4 py-3 text-sm font-bold text-red-600">
                  Sorry, we're not picking up on this date. Please choose another day!
                </p>
              ) : (
                <>
                  <label className="mb-2 mt-5 block text-sm font-extrabold text-brand-green">Time slot *</label>
                  <div className="grid gap-3 sm:grid-cols-2">
                    {slots.map((s) => (
                      <button key={s} data-testid={`slot-${s.replace(/[^a-zA-Z0-9]+/g, "-")}`} onClick={() => setTimeSlot(s)}
                        className={`rounded-2xl border-2 px-4 py-3 text-sm font-extrabold transition-colors duration-200 ${timeSlot === s ? "border-brand-green bg-brand-green text-white" : "border-brand-green/15 text-brand-green hover:border-brand-green/50"}`}>
                        {s}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {step === 4 && (
            <div data-testid="step-contact">
              <h2 className="text-xl font-extrabold text-brand-green">Your details</h2>
              <label className="mb-1 mt-5 block text-sm font-extrabold text-brand-green">Full name *</label>
              <input data-testid="contact-name-input" className={input} value={contact.name}
                onChange={(e) => setContact({ ...contact, name: e.target.value })} placeholder="Jane Smith" />
              <label className="mb-1 mt-4 block text-sm font-extrabold text-brand-green">Email *</label>
              <input data-testid="contact-email-input" type="email" className={input} value={contact.email}
                onChange={(e) => setContact({ ...contact, email: e.target.value })} placeholder="jane@email.com" />
              <label className="mb-1 mt-4 block text-sm font-extrabold text-brand-green">Australian phone *</label>
              <input data-testid="contact-phone-input" className={input} value={contact.phone}
                onChange={(e) => setContact({ ...contact, phone: e.target.value })} placeholder="04XX XXX XXX" />
              {contact.phone && !isAuPhone(contact.phone) && (
                <p data-testid="phone-error" className="mt-1 text-xs font-bold text-red-500">Please enter a valid Australian number, e.g. 04XX XXX XXX</p>
              )}
              <label className="mb-1 mt-4 block text-sm font-extrabold text-brand-green">Notes for our team (optional)</label>
              <textarea data-testid="contact-notes-input" className={`${input} min-h-[80px]`} value={contact.notes}
                onChange={(e) => setContact({ ...contact, notes: e.target.value })} placeholder="Gate code, delicates, allergies…" />
            </div>
          )}

          {step === 5 && (
            <div data-testid="step-confirm">
              <h2 className="text-xl font-extrabold text-brand-green">One last look</h2>
              <div className="mt-5 space-y-3 text-sm text-slate-700">
                {selected.length > 0 && <Row label="Services" value={selected.map((c) => c[0].toUpperCase() + c.slice(1)).join(", ")} />}
                {hasKg && <Row label="Load" value={`~${weight} kg`} />}
                {addonList.map((a) => (
                  <Row key={a.code} label={nameByCode[a.code] || a.code} value={`× ${a.qty}`} />
                ))}
                <Row label="Pick up" value={`${pickupDate} · ${timeSlot}`} />
                <Row label="Address" value={`${address.street}, ${address.suburb} NSW ${address.postcode}`} />
                <Row label="Contact" value={`${contact.name} · ${contact.phone}`} />
              </div>
              <div className="mt-6">
                <p className="mb-3 text-sm font-extrabold text-brand-green">How would you like to pay?</p>
                <div className="grid gap-3 sm:grid-cols-2">
                  <button data-testid="pay-now-option" onClick={() => setPayChoice("now")}
                    className={`rounded-2xl border-2 p-4 text-left transition-colors duration-200 ${payChoice === "now" ? "border-brand-green bg-soft-green" : "border-brand-green/15 hover:border-brand-green/40"}`}>
                    <p className="font-extrabold text-brand-green">Pay now by card</p>
                    <p className="text-xs text-slate-500">Secure Stripe checkout — pay your estimated total today.</p>
                  </button>
                  <button data-testid="pay-later-option" onClick={() => setPayChoice("later")}
                    className={`rounded-2xl border-2 p-4 text-left transition-colors duration-200 ${payChoice === "later" ? "border-brand-green bg-soft-green" : "border-brand-green/15 hover:border-brand-green/40"}`}>
                    <p className="font-extrabold text-brand-green">Pay on delivery</p>
                    <p className="text-xs text-slate-500">Pay by card or cash once we've weighed your laundry.</p>
                  </button>
                </div>
              </div>
              <button data-testid="confirm-booking-btn" onClick={submit} disabled={busy}
                className="btn-bounce mt-6 w-full rounded-full bg-brand-green px-8 py-4 font-extrabold text-white disabled:opacity-60">
                {busy ? (payChoice === "now" ? "Taking you to payment…" : "Booking your pick up…")
                  : (payChoice === "now" ? `Pay $${estimate?.total.toFixed(2) || "0.00"} & Confirm` : `Confirm Booking — $${estimate?.total.toFixed(2) || "0.00"}`)}
              </button>
              <p className="mt-3 text-center text-xs text-slate-400">
                {payChoice === "now"
                  ? "You'll pay the estimated total now by card. If the weighed amount differs, we'll settle the difference on delivery."
                  : "No payment now — pay on delivery once we've weighed your laundry."}
              </p>
            </div>
          )}

          {step < 5 && (
            <div className="mt-8 flex justify-between">
              <button data-testid="wizard-back-btn" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0}
                className="flex items-center gap-1 rounded-full border-2 border-brand-green/20 px-6 py-3 font-extrabold text-brand-green disabled:opacity-40">
                <CaretLeft size={18} weight="bold" /> Back
              </button>
              <button data-testid="wizard-next-btn" onClick={next} disabled={!canNext()}
                className="btn-bounce flex items-center gap-1 rounded-full bg-brand-green px-8 py-3 font-extrabold text-white disabled:opacity-40">
                Next <CaretRight size={18} weight="bold" />
              </button>
            </div>
          )}
        </div>

        {/* Live estimate */}
        <div className="h-fit rounded-3xl bg-brand-green p-6 text-white lg:sticky lg:top-24">
          <h3 className="font-script text-2xl font-bold text-brand-yellow">Live Estimate</h3>
          {estimate && estimate.lines.length > 0 ? (
            <div data-testid="estimate-panel" className="mt-4 space-y-2 text-sm">
              {estimate.lines.map((l) => (
                <div key={l.label} className="flex justify-between gap-2 text-green-50">
                  <span>{l.label}</span><span className="font-bold">${l.amount.toFixed(2)}</span>
                </div>
              ))}
              {estimate.bundle_applied && (
                <p className="rounded-xl bg-brand-yellow/20 px-3 py-2 text-xs font-bold text-brand-yellow">🎉 {estimate.bundle_applied} bundle applied!</p>
              )}
              <div className="flex justify-between border-t border-white/20 pt-2 text-green-50">
                <span>Pick up & delivery</span>
                <span className="font-bold">{estimate.delivery_fee === 0 ? "FREE" : `$${estimate.delivery_fee.toFixed(2)}`}</span>
              </div>
              <div className="flex justify-between border-t border-white/20 pt-2 text-lg font-black">
                <span>Total</span><span data-testid="estimate-total">${estimate.total.toFixed(2)}</span>
              </div>
              <p className="text-xs text-green-200">FREE pick up & delivery within 10 km of Hebersham when your order is over ${estimate.free_delivery_over.toFixed(0)}.</p>
            </div>
          ) : (
            <p className="mt-4 text-sm text-green-100">Choose your services and items to see your price here — updated live as you go!</p>
          )}
        </div>
      </div>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-4 rounded-xl bg-brand-cream px-4 py-2.5">
    <span className="font-extrabold text-brand-green">{label}</span>
    <span className="text-right">{value}</span>
  </div>
);

const QtyRow = ({ item, value, onChange }) => (
  <div className="flex items-center justify-between gap-3 rounded-2xl bg-soft-yellow px-4 py-3" data-testid={`item-${item.code}`}>
    <div>
      <p className="text-sm font-extrabold text-brand-green">{item.name}</p>
      <p className="text-xs text-slate-500">{item.price_max ? `from $${item.price.toFixed(2)}` : `$${item.price.toFixed(2)}`} each</p>
    </div>
    <div className="flex items-center gap-3">
      <button data-testid={`item-${item.code}-minus`} onClick={() => onChange(value - 1)}
        className="flex h-9 w-9 items-center justify-center rounded-full bg-white text-brand-green shadow"><Minus size={16} weight="bold" /></button>
      <span data-testid={`item-${item.code}-count`} className="w-8 text-center text-lg font-black text-brand-green">{value}</span>
      <button data-testid={`item-${item.code}-plus`} onClick={() => onChange(value + 1)}
        className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-green text-white shadow"><Plus size={16} weight="bold" /></button>
    </div>
  </div>
);
